﻿import * as $ from 'jquery';
import { serialDisposable, disposable } from 'common/disposable';
import { bindStyle, bindAttribute, bindDisplay, bindCss, bindText, bindVal, bindHeight } from 'common/dom/fluency';
import { Observable, ISubscription } from 'common/barrels/rx';
import { Color } from 'common/color';
import { enterPressed, escapePressed, click } from 'common/rxui/core';
import { IDisposable } from 'common/barrels/core';
import { addTimePickerToJQuery } from 'common/libs/timepicker/jquery.timepicker';

declare global {
    export interface JQuery {
        bindDisplay: (obs: Observable<boolean>) => JQuery;
        bindStyle: (obs: Observable<boolean>, trueClass: string, falseClass: string) => JQuery;
        bindAttribute<T>(obs: Observable<T>, attributeName: string): JQuery;
        bindContent<T>(obs: Observable<T>, contentFactory: (x: T) => JQuery[]): JQuery;
        bindPartialContent<T>(obs: Observable<T>, contentFactory: (x: T) => JQuery[]): JQuery;
        bindCss(obs: Observable<string>, propertyName: string): JQuery;
        bindText(value: string | Observable<string>): JQuery;
        bindVal(value: string | Observable<string>): JQuery;
        bindHeight(obs: Observable<number>): JQuery;
        clicks: (subscribe: (o: Observable<MouseEvent>, e: JQuery) => ISubscription) => JQuery;
        dblclicks: (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) => JQuery;
        changes: (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) => JQuery;
        owns: (subscription: ISubscription| IDisposable) => JQuery;
        applyBackground(argb: string): JQuery;
        conditionalAttr(condition: boolean, attrName: string, value?: string | (() => string)): JQuery;
        conditional(condition: boolean, applicator: (x) => void): JQuery;
        conditionalClass(condition: boolean, className: string): JQuery;
        conditionalCss(condition: boolean | string, propertyName: string, value?: string | (() => string)): JQuery;

        takeFocus(): JQuery;
        enterKey(subscribe: (o: Observable<any>, e: JQuery) => ISubscription): JQuery;
        escapeKey(subscribe: (o: Observable<any>, e: JQuery) => ISubscription): JQuery;

        clickOrEscape(subscribe: (o: Observable<any>, e: JQuery) => ISubscription): JQuery;
        clickOrEnter(subscribe: (o: Observable<any>, e: JQuery) => ISubscription): JQuery;
    }
}

let augmented = false;

export function revertPrefilter() {
    const rxhtmlTag = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([a-z][^\/\0>\x20\t\r\n\f]*)[^>]*)\/>/gi;
    (<any>$).htmlPrefilter = function(html) {
        return html.replace(rxhtmlTag, "<$1></$2>" );
    };
}

export function fixBrokenCleanData() {
    //https://github.com/jquery/jquery-ui/issues/1990
    (<any>$).cleanData = ( function( orig ) {
        return function( elems ) {
            var events, elem, i;
            for ( i = 0; ( elem = elems[ i ] ) != null; i++ ) {
                try {
    
                    // Only trigger remove when necessary to save time
                    events = (<any>$)._data( elem, "events" );
                    if ( events && events.remove ) {
                        $( elem ).triggerHandler( "remove" );
                    }
                // Http://bugs.jquery.com/ticket/8235
                } catch ( e ) {}
            }
            orig( elems );
        };
    } )( (<any>$).cleanData );
}

export function augmentJQuery() {
    if (augmented)
        return;

    augmented = true;

    addTimePickerToJQuery($);

    const root = (<any>$).prototype;

    root.bindDisplay = function (obs: Observable<boolean>) {
        return bindDisplay(this, obs);
    }

    root.bindStyle = function (obs: Observable<boolean>, trueClass: string, falseClass: string) {
        return bindStyle(this, obs, trueClass, falseClass);
    };

    root.bindAttribute = function<T>(obs: Observable<T>, attributeName: string) {
        return bindAttribute(this, obs, attributeName);
    };

    root.bindContent = function <T>(obs: Observable<T>, contentFactory: (x: T) => JQuery[]) {
        const subscription = obs.subscribe(x => {
            // it is important in some scenarios to construct the new elements before clearing the old to prevent .remove() being called on some reused subelements
            const content = contentFactory(x);
            this.empty().append(content);
        });

        this.on('remove', () => subscription.unsubscribe());

        return this;
    };

    root.bindPartialContent = function <T>(obs: Observable<T>, contentFactory: (x: T) => JQuery[]) {
        const d = serialDisposable(null);

        const subscription = obs.subscribe(x => {
            const content = contentFactory(x);
            d.set(disposable(() => content.forEach(y => y.remove())));
            this.append(content);
        });

        this.on('remove', () => subscription.unsubscribe());

        return this;
    };

    root.bindText = function (value: string | Observable<string>) {
        return bindText(this, value);
    }

    root.bindVal = function (value: string | Observable<string>) {
        return bindVal(this, value);
    }

    root.bindHeight = function (obs: Observable<number>) {
        return bindHeight(this, obs);
    }

    root.bindCss = function (obs: Observable<string>, propertyName: string) {
        return bindCss(this, obs, propertyName);
    };

    root.clicks = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(Observable.fromEvent<MouseEvent>(this, 'click'), this));
    };

    root.dblclicks = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(Observable.fromEvent(this, 'dblclick'), this));
    };

    root.changes = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(Observable.fromEvent(this, 'change'), this));
    };

    root.owns = function (resource: ISubscription | IDisposable) {
        return this.on('remove', () => {
            if ((<IDisposable>resource).dispose)
                (<IDisposable>resource).dispose();
            else
                (<ISubscription>resource).unsubscribe();
        });
    };

    root.applyBackground = function (argb: string) {
        Color.applyBackground(this, argb);

        return this;
    };

    root.conditionalAttr = function (condition: boolean, attrName: string, value?: string | (() => string)) {
        if (condition) {
            var v = value === undefined ? condition
                : $.isFunction(value) ? (<() => string>value)()
                    : value;

            this.attr(attrName, v);
        }

        return this;
    }

    root.conditional = function (condition: boolean, applicator: (x) => void): JQuery {
        if (condition)
            applicator(this);

        return this;
    };

    root.conditionalClass = function (condition: boolean, className: string) {
        if (condition)
            this.addClass(className);

        return this;
    };

    root.conditionalCss = function (condition: boolean | string, propertyName: string, value?: string | (() => string)) {
        if (condition) {
            const v = value === undefined ? condition
                    : $.isFunction(value) ? (<() => string>value)()
                    : value;

            this.css(propertyName, v);
        }

        return this;
    };

    root.takeFocus = function () {
        this.owns(Observable.timer().subscribe(() => this.focus()));

        return this;
    };

    root.enterKey = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(enterPressed(this), this));
    };

    root.escapeKey = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(escapePressed(this), this));
    };

    root.clickOrEnter = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(click(this).merge(enterPressed($(document))), this));
    };

    root.clickOrEscape = function (subscribe: (o: Observable<any>, e: JQuery) => ISubscription) {
        return this.owns(subscribe(click(this).merge(escapePressed($(document))), this));
    };
}